import {
  forwardRef,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  type ComponentType,
  type ForwardedRef,
} from 'react';

// Attributes that reference a <defs> entry by `url(#id)`.
const URL_REFERENCE_ATTRS = ['fill', 'stroke', 'clip-path', 'mask', 'filter'];

const renameIds = (svg: SVGSVGElement, suffix: string) => {
  svg.querySelectorAll('[id]').forEach((def) => {
    if (def.id.endsWith(`-${suffix}`)) return;
    const from = `url(#${def.id})`;
    def.id = `${def.id}-${suffix}`;
    const to = `url(#${def.id})`;
    svg.querySelectorAll('*').forEach((node) => {
      URL_REFERENCE_ATTRS.forEach((attr) => {
        if (node.getAttribute(attr) === from) node.setAttribute(attr, to);
      });
    });
  });
};

const assignRef = <T,>(ref: ForwardedRef<T>, value: T | null) => {
  if (typeof ref === 'function') ref(value);
  else if (ref) ref.current = value;
};

/**
 * `@kula-ai/assets` brand marks hard-code their <defs> ids — ZincAsset's green
 * hexagon is `fill="url(#kula-brand-zinc__paint0_linear_3101_60322)"`, the same
 * string in every copy. A browser resolves `url(#id)` to the FIRST element with
 * that id in the document, and a gradient inside a `display: none` subtree (a
 * collapsed interview-plan stage) does not paint — so every visible copy after
 * it renders as a bare black tile.
 *
 * This gives each mounted copy its own ids. Checked: the assets take no id or
 * prefix prop (0.3.6) — a per-instance id belongs upstream in `@kula-ai/assets`;
 * drop this wrapper once it lands.
 */
export const withUniqueSvgIds = <P extends object>(Asset: ComponentType<P>) => {
  const Unique = forwardRef<HTMLSpanElement, P>((props, forwardedRef) => {
    const wrapperRef = useRef<HTMLSpanElement | null>(null);
    const suffix = useId().replace(/[^a-zA-Z0-9_-]/g, '');

    const setRef = useCallback(
      (node: HTMLSpanElement | null) => {
        wrapperRef.current = node;
        assignRef(forwardedRef, node);
      },
      [forwardedRef]
    );

    // Every commit, not once: React never rewrites an attribute whose prop is
    // unchanged, so a renamed id survives a re-render — but a remounted <svg>
    // arrives with the shared ids again. `renameIds` skips ids already done.
    useLayoutEffect(() => {
      const svg = wrapperRef.current?.querySelector('svg');
      if (svg) renameIds(svg, suffix);
    });

    return (
      // `display: contents` keeps the wrapper out of layout: the <svg> still
      // sizes and aligns as the caller's direct child.
      <span ref={setRef} style={{ display: 'contents' }}>
        <Asset {...(props as P)} />
      </span>
    );
  });
  Unique.displayName = `withUniqueSvgIds(${Asset.displayName || Asset.name || 'Asset'})`;
  return Unique;
};
